import React, { TextareaHTMLAttributes, forwardRef } from 'react';
import { TextareaAutosize } from '@mui/material';
import styled from 'styled-components';
import { scale } from 'tuui/lib/utils/styles';
import { UIC } from 'tuui/lib/utils/typing';

export interface TextareaProps {
    // Fixed height mode
    rows?: number;
    // Auto-resize mode
    minRows?: number;
    maxRows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & TextareaProps>(
    (
        {
            placeholder = 'Enter your text here...',
            rows,
            minRows,
            maxRows,
            value,
            className,
            onChange,
            ...rest
        },
        ref
    ) => {
        const isAutoResize = minRows !== undefined || maxRows !== undefined;

        let calculatedMinRows: number;
        let calculatedMaxRows: number;

        if (isAutoResize) {
            calculatedMinRows = minRows ?? 1;
            calculatedMaxRows = maxRows ?? Math.max(10, (minRows ?? 1) * 2); 
        } else {
            const fixedRows = rows ?? 5;
            calculatedMinRows = fixedRows;
            calculatedMaxRows = fixedRows;
        }

        // Validation: ensure maxRows >= minRows
        const validatedMaxRows = Math.max(calculatedMaxRows, calculatedMinRows);

        return (
                <StyledTextareaAutosize
                    ref={ref}
                    {...rest}
                    value={value ?? ''}
                    className={className}
                    minRows={calculatedMinRows}
                    maxRows={validatedMaxRows}
                    placeholder={placeholder}
                    onChange={onChange}
                />
        );
    }
) as UIC<TextareaHTMLAttributes<HTMLTextAreaElement> & TextareaProps>;

const StyledTextareaAutosize = styled(TextareaAutosize)`
    width: 100%;
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.sizes.lg};
    line-height: 1.4; // Explicit line-height ensures Material UI calculates rows accurately
    border-radius: ${scale('xs')};
    padding: ${scale('sm')} ${scale('md')}; // Reduced vertical padding for better row calculations
    resize: none;
    border: ${scale('xxxs')} solid ${({ theme }) => theme.palette.grayscale[5]};

    ::placeholder {
        color: ${({ theme }) => theme.palette.grayscale[6]};
        font-size: ${({ theme }) => theme.typography.sizes.lg};
    }
`;
